import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../i18n/lingua.js';
import { chiama } from './cliente.js';

/**
 * Le integrazioni con Business Central.
 *
 * La pagina risponde a una domanda sola — **gli import girano?** — e la
 * risposta è una data, non un semaforo. Nel v2 non c'è modo di chiederlo, e
 * infatti l'orologio degli articoli della Inc è fermo al 31 dicembre 2023.
 */

export interface FlussoBc {
  key: string;
  name: string;
  description: string;
  lastRunAt: string | null;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  runs: number;
  /** Fin dove è arrivata la lettura: le righe toccate dopo questa data. */
  cursor: string | null;
}

export interface EsecuzioneBc {
  id: number;
  stream: string;
  startedAt: string;
  finishedAt: string | null;
  status: 'running' | 'succeeded' | 'failed';
  rowsRead: number;
  rowsApplied: number;
  rowsRejected: number;
  error: string | null;
}

export interface RigaEsecuzione {
  id: number;
  externalKey: string | null;
  action: string;
  message: string | null;
  payload: Record<string, unknown>;
  createdAt: string;
}

export interface EsitoEsecuzione {
  runId: number;
  stream: string;
  rowsRead: number;
  rowsApplied: number;
  rowsRejected: number;
  created: number;
  updated: number;
  skipped: number;
  status: 'succeeded' | 'failed';
  error: string | null;
  cursor: string | null;
}

/**
 * Gli orari: quando ogni flusso parte da solo.
 *
 * `lastStatus` non è lo stato dell'ultima esecuzione qualunque, è quello che
 * decide se parte un'e-mail: `rejected` vuol dire che è andata, ma con righe
 * scartate — e le righe scartate qualcuno le deve guardare.
 */
export interface OrarioImport {
  stream: string;
  name: string;
  description: string;
  everyMinutes: number | null;
  enabled: boolean;
  nextRunAt: string | null;
  lastRunAt: string | null;
  lastStatus: 'succeeded' | 'failed' | 'rejected' | null;
  problemSince: string | null;
  alertedAt: string | null;
}

export interface Orari {
  schedules: OrarioImport[];
  /** Zero vuol dire che nessuno riceverà gli allarmi. */
  alertRecipients: number;
  /** Falso quando il servizio gira senza orologio. */
  clockRunning: boolean;
}

export function useOrariImport() {
  return useQuery({
    queryKey: ['orari-import'],
    queryFn: () => chiama<Orari>('/api/integrations/schedules'),
    /** Mentre si guarda la pagina, l'orologio gira: si rilegge da solo. */
    refetchInterval: 30_000,
  });
}

export function useCambiaOrario() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      stream,
      ...dati
    }: {
      stream: string;
      enabled?: boolean;
      everyMinutes?: number;
    }) =>
      chiama<{ stream: string; enabled: boolean; everyMinutes: number }>(
        `/api/integrations/schedules/${stream}`,
        { metodo: 'PUT', corpo: dati },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['orari-import'] });
    },
  });
}

/** Lancia un flusso qualunque adesso — e se è rotto, l'allarme parte da qui. */
export function useLanciaFlusso() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (stream: string) =>
      chiama<{
        stream: string;
        name: string;
        rowsRead: number;
        rowsApplied: number;
        rowsRejected: number;
        status: 'succeeded' | 'failed';
        error: string | null;
        alert: 'allarme' | 'rientro' | null;
      }>(`/api/integrations/run/${stream}`, { metodo: 'POST' }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['orari-import'] });
      void client.invalidateQueries({ queryKey: ['integrazioni-bc'] });
      void client.invalidateQueries({ queryKey: ['integrazioni-bc-esecuzioni'] });
      void client.invalidateQueries({ queryKey: ['vettori'] });
    },
  });
}

export function useFlussiBc() {
  return useQuery({
    queryKey: ['integrazioni-bc'],
    queryFn: () =>
      chiama<{ streams: FlussoBc[]; counts: Record<string, number> }>('/api/integrations/bc'),
  });
}

export function useEsecuzioniBc() {
  return useQuery({
    queryKey: ['integrazioni-bc-esecuzioni'],
    queryFn: () => chiama<{ runs: EsecuzioneBc[] }>('/api/integrations/bc/runs'),
  });
}

export function useRigheEsecuzione(runId: number | null) {
  return useQuery({
    queryKey: ['integrazioni-bc-righe', runId],
    queryFn: () => chiama<{ rows: RigaEsecuzione[] }>(`/api/integrations/bc/runs/${runId}/rows`),
    enabled: runId !== null,
  });
}

export interface FlussoVettore {
  key: 'interglobo' | 'cl_pod' | 'campbell_po';
  name: string;
  description: string;
  /** Da dove si legge adesso: l'SFTP, la casella, o una cartella. */
  source: string;
}

export interface EsitoScarico {
  runId: number;
  stream: string;
  read: number;
  applied: number;
  rejected: number;
  skipped: number;
  /** PO di Campbell arrivati prima del loro ordine: si riprovano al giro dopo. */
  waiting: number;
  alerts: number;
  notified: number;
  /** Gli ordini rimandati a Business Central, e quanti di questi non sono andati. */
  bcSent: number;
  bcProblems: number;
  status: 'succeeded' | 'failed';
  error: string | null;
}

/** «Aggiorna gli stati in BC» mentre gira. */
export interface LavoroAggiornaStati {
  runId: number;
  from: string;
  total: number;
  done: number;
  ok: number;
  problems: number;
  startedAt: string;
}

export interface StatoAggiornaStati {
  mode: 'spento' | 'prova' | 'acceso';
  /** Falso per la Inc: i suoi stati in BC li porta avanti chi ci lavora. */
  spa: boolean;
  running: LavoroAggiornaStati | null;
  /** Quanti ordini partirebbero dalla data chiesta; `null` se non si è chiesta. */
  orders: number | null;
}

/**
 * A che punto è l'aggiornamento degli stati, e quanti ordini partirebbero
 * da una data. Mentre il lavoro gira si rilegge da solo, e quando finisce
 * si rileggono anche le esecuzioni: è lì che resta com'è andata.
 */
export function useAggiornaStati(dal: string) {
  const client = useQueryClient();
  return useQuery({
    queryKey: ['aggiorna-stati', dal],
    queryFn: async () => {
      const prima = client.getQueryData<StatoAggiornaStati>(['aggiorna-stati', dal]);
      const stato = await chiama<StatoAggiornaStati>(
        `/api/integrations/bc/status-refresh${dal ? `?from=${encodeURIComponent(dal)}` : ''}`,
      );
      if (stato.running || prima?.running) {
        void client.invalidateQueries({ queryKey: ['integrazioni-bc-esecuzioni'] });
      }
      return stato;
    },
    refetchInterval: (query) => (query.state.data?.running ? 5_000 : false),
  });
}

export function useLanciaAggiornaStati() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (dal: string) =>
      chiama<{ running: LavoroAggiornaStati }>('/api/integrations/bc/status-refresh', {
        metodo: 'POST',
        corpo: { from: dal },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['aggiorna-stati'] });
      void client.invalidateQueries({ queryKey: ['integrazioni-bc-esecuzioni'] });
    },
  });
}

export function useFermaAggiornaStati() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      chiama<{ stopping: boolean }>('/api/integrations/bc/status-refresh/stop', {
        metodo: 'POST',
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['aggiorna-stati'] });
    },
  });
}

export function useFlussiVettori() {
  return useQuery({
    queryKey: ['vettori'],
    queryFn: () => chiama<{ streams: FlussoVettore[] }>('/api/carriers'),
    staleTime: 5 * 60_000,
  });
}

export function useScaricaVettore() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (chiave: string) =>
      chiama<EsitoScarico>(`/api/carriers/${chiave}/fetch`, { metodo: 'POST' }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['integrazioni-bc'] });
      void client.invalidateQueries({ queryKey: ['integrazioni-bc-esecuzioni'] });
      void client.invalidateQueries({ queryKey: ['avvisi'] });
      void client.invalidateQueries({ queryKey: ['documenti'] });
    },
  });
}

export function useLanciaImport() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (dati: { stream: string; dryRun?: boolean; fromScratch?: boolean }) =>
      chiama<EsitoEsecuzione>('/api/integrations/bc/run', { metodo: 'POST', corpo: dati }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['integrazioni-bc'] });
      void client.invalidateQueries({ queryKey: ['integrazioni-bc-esecuzioni'] });
      void client.invalidateQueries({ queryKey: ['articoli'] });
      void client.invalidateQueries({ queryKey: ['anagrafiche'] });
    },
  });
}

/**
 * Da quanto non gira.
 *
 * Non un numero di ore: una frase. «Due giorni fa» dice quello che serve,
 * «48,3 ore» no.
 */
export function daQuanto(quando: string | null): string {
  if (!quando) return t('mai');
  const minuti = Math.floor((Date.now() - new Date(quando).getTime()) / 60_000);
  if (minuti < 2) return t('adesso');
  if (minuti < 60) return t('{n} minuti fa', { n: minuti });
  const ore = Math.floor(minuti / 60);
  if (ore < 24) return ore === 1 ? t('un’ora fa') : t('{n} ore fa', { n: ore });
  const giorni = Math.floor(ore / 24);
  if (giorni < 30) return giorni === 1 ? t('ieri') : t('{n} giorni fa', { n: giorni });
  const mesi = Math.floor(giorni / 30);
  if (mesi < 24) return mesi === 1 ? t('un mese fa') : t('{n} mesi fa', { n: mesi });
  return t('{n} anni fa', { n: Math.floor(mesi / 12) });
}

/**
 * Quando un flusso va guardato.
 *
 * Un import che deve girare ogni giorno e non gira da due è la cosa che
 * questa pagina esiste per dire. La soglia è larga apposta: meglio non
 * gridare per un ritardo di qualche ora.
 */
export function inRitardo(flusso: FlussoBc): boolean {
  if (!flusso.lastSuccessAt) return true;
  const giorni = (Date.now() - new Date(flusso.lastSuccessAt).getTime()) / 86_400_000;
  return giorni > 2;
}
